import { mutate } from './api'
import { undoPatch } from './undo'
import { buzz } from './haptic'
import { el } from './ui'
import { parkingNotesOn } from './business-settings'
import { parkingHours, parkingWords } from './parking-hours'
import type { TodayClean } from './types'

// A customer's notes on a Today card ("Access & notes"): the job notes, parking, preferred times and what to say when
// calling. The owner can edit or remove one right there when it is out of date (Liam, 13 Oct 2026: "as the owner and
// worker for today i should be able to edit or dismiss notes because they're out of date"). It is the customer's own
// note, so the change shows on every visit of theirs, saved as the customer page saves it, with Undo. Workers see the
// notes as before. The old words are kept at the foot of the customer's Notes, dated (Liam: "do those old ones get saved
// somewhere"; functions/api/customers/[id].ts keptNotes), and a note can be swiped left to dismiss it ("a swipe feature
// which is not hard delete ... won't be on this one or going forward"): the same as Remove note, with Undo.

export type NoteField = 'job_notes' | 'parking_restrictions' | 'preferred_times' | 'call_notes'
export interface AccessNote { field: NoteField; text: string }

const LABEL: Record<NoteField, string> = { job_notes: '', parking_restrictions: 'Parking: ', preferred_times: 'Preferred times: ', call_notes: 'When calling: ' }
const NAME: Record<NoteField, string> = { job_notes: 'note', parking_restrictions: 'parking note', preferred_times: 'preferred times', call_notes: 'calling note' }

export function accessNoteItems(cl: TodayClean): AccessNote[] {
  return (['job_notes', 'parking_restrictions', 'preferred_times', 'call_notes'] as NoteField[])
    .map((field) => ({ field, text: (cl[field] ?? '').trim() }))
    .filter((n) => n.text !== '')
}

export function noteLine(n: AccessNote): string {
  return `${LABEL[n.field]}${n.text}`
}

// One note as a line. For the owner (the card carries the customer's version): Edit opens it in place (Save, Remove
// note, Cancel) and Hide puts it away. On a card's notes box each note is a tile that can also be swiped left to hide it,
// iPhone Mail's way (Liam, 13 Oct 2026: "the slide to dismiss tile animation is not good or satisfying plus there is no
// hide icon or something to indicate what's happening"): a slate Hide panel shows behind the tile as it moves, the icon
// grows with a tap of the phone once letting go will hide it, and the tile slides off and the gap closes before the Undo
// bar. onChanged reads the day again once the change is saved or undone.
export function noteRow(n: AccessNote, cl: TodayClean, owner: boolean, onChanged: () => void, tag: 'p' | 'span' = 'p'): HTMLElement {
  const line = el(tag, { class: 'access-note' }, noteLine(n))
  if (!owner || typeof cl.customer_version !== 'number') return line
  const edit = el('button', { type: 'button', class: 'link-button access-note-edit', 'aria-label': `Edit the ${NAME[n.field]} for ${cl.customer_name}` }, 'Edit')
  const hide = el('button', { type: 'button', class: 'link-button access-note-hide', 'aria-label': `Hide the ${NAME[n.field]} for ${cl.customer_name}` }, 'Hide')
  line.append(' ', edit, ' ', hide)
  // Refused (another phone changed the customer): the day is read again, so the note comes back as it now is.
  const away = (): void => { void saveNote(n, cl, null, 'Note hidden', onChanged).then((problem) => { if (problem) onChanged() }) }
  // A line in the started card's one-line notes stays a line: Edit and Hide, no tile to swipe.
  if (tag === 'span') {
    edit.addEventListener('click', (e) => { e.stopPropagation(); line.replaceWith(editor(n, cl, line, onChanged)) })
    hide.addEventListener('click', (e) => { e.stopPropagation(); away() })
    return line
  }
  line.classList.add('access-note-swipe')
  const action = el('div', { class: 'access-note-action', 'aria-hidden': 'true' }, el('span', { class: 'access-note-action-icon ic ic-eye-off' }), el('span', {}, 'Hide'))
  const row = el('div', { class: 'access-note-row' }, action, line)
  edit.addEventListener('click', (e) => { e.stopPropagation(); row.replaceWith(editor(n, cl, row, onChanged)) })
  hide.addEventListener('click', (e) => { e.stopPropagation(); void slideAway(row, line).then(away) })
  swipeAway(row, line, () => { void slideAway(row, line).then(away) })
  return row
}

// The tile follows the finger left over its Hide panel. Past 40% of its width (80 to 140 points) the panel is armed:
// its icon grows and the phone taps once; let go there and it hides, short of it the tile springs back. Any speed.
// Today's own day swipe leaves notes alone (today.ts NO_SWIPE). A tile is never left part way across (Liam's iPhone,
// 13 Oct 2026: a note stuck three quarters off the card with only its Edit showing): once it moves it holds the finger
// until it lets go, and letting go anywhere on the screen puts back any tile not on its way off.
function swipeAway(row: HTMLElement, line: HTMLElement, onAway: () => void): void {
  putBackStrayTiles()
  let start: { x: number; y: number; id: number } | null = null
  let armed = false
  const springBack = (): void => {
    start = null
    armed = false
    row.classList.remove('access-note-dragging', 'access-note-armed')
    if (!row.classList.contains('access-note-going')) line.style.transform = ''
  }
  const far = (): number => Math.min(140, Math.max(80, (row.getBoundingClientRect().width || 240) * 0.4))
  row.addEventListener('pointerdown', (e: PointerEvent) => {
    if (e.pointerType !== 'touch' || (e.target as Element | null)?.closest?.('button, a, textarea, input')) return
    // A second finger while the tile is moving is left alone: the first finger's lift still ends it (Astra, 21 Oct 2026).
    if (start && e.pointerId !== start.id && row.classList.contains('access-note-dragging')) return
    start = { x: e.clientX, y: e.clientY, id: e.pointerId }
    armed = false
  })
  row.addEventListener('pointermove', (e: PointerEvent) => {
    if (!start || e.pointerId !== start.id) return
    const dx = Math.min(0, e.clientX - start.x)
    if (Math.abs(dx) <= Math.abs(e.clientY - start.y)) return
    if (!row.classList.contains('access-note-dragging')) { try { row.setPointerCapture(e.pointerId) } catch { /* the finger is already gone */ } }
    row.classList.add('access-note-dragging')
    line.style.transform = `translateX(${dx}px)`
    const now = -dx > far()
    if (now !== armed) {
      armed = now
      row.classList.toggle('access-note-armed', armed)
      if (armed) buzz('light')
    }
  })
  const end = (e: PointerEvent): void => {
    const s = start
    // Another finger's lift is not this swipe's end.
    if (!s || e.pointerId !== s.id) return
    start = null
    row.classList.remove('access-note-dragging')
    const dx = e.clientX - s.x, dy = e.clientY - s.y
    if (-dx > far() && Math.abs(dx) > Math.abs(dy) * 1.5) onAway()
    else springBack()
  }
  row.addEventListener('pointerup', end)
  row.addEventListener('pointercancel', (e: PointerEvent) => { if (!start || e.pointerId === start.id) springBack() })
  // Only the row's own hold on the finger, taken once the tile moves. An iPhone lets go of the finger's first hold (on
  // the words) the moment it lands; read as the end, it stopped every swipe before it began (Liam's iPhone, 21 Oct 2026:
  // "I can't swipe to dismiss them"; seen on the simulator).
  row.addEventListener('lostpointercapture', (e: PointerEvent) => {
    if (start && e.target === row && e.pointerId === start.id && row.classList.contains('access-note-dragging')) springBack()
  })
}

// Once for the page: a finger lifted anywhere puts back a tile left part way across that is not sliding off.
let strayWatch = false
function putBackStrayTiles(): void {
  if (strayWatch) return
  strayWatch = true
  const sweep = (): void => {
    setTimeout(() => {
      for (const row of document.querySelectorAll<HTMLElement>('.access-note-row:not(.access-note-going):not(.access-note-dragging)')) {
        const tile = row.querySelector<HTMLElement>('.access-note-swipe')
        if (tile?.style.transform) { tile.style.transform = ''; row.classList.remove('access-note-armed') }
      }
    }, 60)
  }
  document.addEventListener('touchend', (e) => { if (!e.touches.length) sweep() }, { capture: true, passive: true })
  document.addEventListener('touchcancel', sweep, { capture: true, passive: true })
}

// The tile slides off to the left, then the space it took closes, then the note is put away (Hide, or a swipe).
function slideAway(row: HTMLElement, line: HTMLElement): Promise<void> {
  row.classList.add('access-note-armed', 'access-note-going')
  line.style.transform = 'translateX(-110%)'
  return new Promise((done) => {
    setTimeout(() => {
      row.style.height = `${row.getBoundingClientRect().height || 0}px`
      void row.getBoundingClientRect()
      row.classList.add('access-note-closing')
      row.style.height = '0px'
      setTimeout(done, 200)
    }, 170)
  })
}

// Saves a note's new words (null: removed), keeping the old words in Notes, then offers Undo. Answers the error words,
// or null once saved or waiting for signal.
async function saveNote(n: AccessNote, cl: TodayClean, value: string | null, done: string, onChanged: () => void): Promise<string | null> {
  const version = cl.customer_version as number
  const path = `/api/customers/${cl.customer_id}`
  // With the note as this card showed it (same-time saves): it saves unless someone changed this same note first.
  const ack = await mutate({ label: `${done}: ${cl.customer_name}`, method: 'PATCH', path, body: { [n.field]: value, version, keep_old_note: true, base: { [n.field]: cl[n.field] ?? null } } })
  if (!ack.ok && ack.status !== 0) {
    return ack.status === 409 ? 'This customer was changed on another phone. Close this and look again.' : ack.error ?? "That didn't save."
  }
  cl[n.field] = value
  undoPatch({ text: `${done} for ${cl.customer_name}`, label: `Undo: ${done.toLowerCase()} for ${cl.customer_name}`, path, ack, version, body: { [n.field]: n.text, restore_old_note: true }, after: onChanged })
  onChanged()
  return null
}

// `adding`: a new note in an empty box (Parking notes, 0084): headed with what it is, an example in it, no Remove.
function editor(n: AccessNote, cl: TodayClean, line: HTMLElement, onChanged: () => void, adding = false): HTMLElement {
  const box = el('div', { class: 'access-note-editor' })
  const input = el('textarea', { class: 'access-note-input', rows: '3', maxlength: '2000', 'aria-label': `The ${NAME[n.field]} for ${cl.customer_name}` }) as HTMLTextAreaElement
  input.value = n.text
  if (adding) {
    input.placeholder = EXAMPLE[n.field]
    box.append(el('strong', { class: 'access-note-adding' }, ADD_LABEL[n.field]))
  }
  // Parking (with Parking notes on): the hours Roundly read from the words, said as they are typed, so it is plain when a
  // booking in them will be asked about (docs/plans/job-bits/PLAN.md 1).
  const hoursLine = n.field === 'parking_restrictions' && parkingNotesOn() ? el('p', { class: 'access-note-hours', 'aria-live': 'polite' }) : null
  const paintHours = (): void => {
    if (!hoursLine) return
    const h = parkingHours(input.value)
    hoursLine.textContent = h ? `Roundly will check bookings against ${parkingWords(h)}.` : ''
  }
  input.addEventListener('input', paintHours)
  paintHours()
  const err = el('p', { class: 'form-error', role: 'alert' })
  const save = el('button', { type: 'button', class: 'btn-primary' }, 'Save')
  const remove = el('button', { type: 'button', class: 'btn-secondary' }, 'Remove note')
  const cancel = el('button', { type: 'button', class: 'link-button' }, 'Cancel')
  const send = (value: string | null, done: string): void => {
    for (const b of [save, remove]) b.setAttribute('disabled', 'disabled')
    err.textContent = ''
    void saveNote(n, cl, value, done, onChanged).then((problem) => {
      if (!problem) return
      for (const b of [save, remove]) b.removeAttribute('disabled')
      err.textContent = problem
    })
  }
  save.addEventListener('click', () => {
    const value = input.value.trim()
    if (value === n.text) { box.replaceWith(line); return }
    send(value || null, value ? 'Note saved' : 'Note removed')
  })
  remove.addEventListener('click', () => send(null, 'Note removed'))
  cancel.addEventListener('click', () => box.replaceWith(line))
  box.append(input, ...(hoursLine ? [hoursLine] : []), el('div', { class: 'access-note-acts' }, ...(adding ? [save, cancel] : [save, remove, cancel])), err)
  setTimeout(() => input.focus(), 0)
  return box
}

// ---- Parking notes (0084, an Extra feature, off as standard) ---------------------------------------------------------
// Liam, 22 Oct 2026: "Ive uploaded some more parking restriction time into notes but there was nowhere to add parking
// restritions in this", then design A (https://claude.ai/artifact/MXe9yihgJ7AnGsth2buB6N). On the owner's Today card, at
// the foot of Access & notes (or on its own where there are none): "+ Add parking or times". It offers only the boxes the
// customer has empty (Parking, Preferred times, When calling), then the same editor and save as Edit, headed with what it
// is. What is added is the customer's own note, so it shows on their page (At the door), in the Diary and on Today.
const ADD_LABEL: Record<NoteField, string> = { job_notes: 'Job note', parking_restrictions: 'Parking', preferred_times: 'Preferred times', call_notes: 'When calling' }
const EXAMPLE: Record<NoteField, string> = {
  job_notes: 'For example: side gate code 1984',
  parking_restrictions: 'For example: no parking 11am to 1pm, pay and display',
  preferred_times: 'For example: Monday, Wednesday or Friday mornings',
  call_notes: 'For example: ring the house phone, not the mobile',
}
const ADDABLE: NoteField[] = ['parking_restrictions', 'preferred_times', 'call_notes']

export function addNoteLink(cl: TodayClean, onChanged: () => void): HTMLElement | null {
  const empty = ADDABLE.filter((f) => !(cl[f] ?? '').trim())
  if (!empty.length || typeof cl.customer_version !== 'number') return null
  const wrap = el('div', { class: 'access-note-add' })
  const open = el('button', { type: 'button', class: 'link-button access-note-add-link', 'aria-expanded': 'false' },
    empty.includes('parking_restrictions') ? '+ Add parking or times' : '+ Add a note')
  const chips = el('div', { class: 'access-note-chips', role: 'group', 'aria-label': `What to add for ${cl.customer_name}` })
  chips.hidden = true
  // The editor, or the empty place it leaves when its own Cancel puts its "line" back.
  let placed: HTMLElement | null = null
  const shut = (): void => { chips.hidden = true; open.hidden = false; open.setAttribute('aria-expanded', 'false'); placed?.remove(); wrap.querySelector('.access-note-editor')?.remove() }
  for (const f of empty) {
    const chip = el('button', { type: 'button', class: 'access-note-chip' }, ADD_LABEL[f])
    chip.addEventListener('click', (e) => {
      e.stopPropagation()
      placed?.remove()
      wrap.querySelector('.access-note-editor')?.remove()
      for (const c of Array.from(chips.querySelectorAll('.access-note-chip'))) c.classList.toggle('access-note-chip-on', c === chip)
      // Cancel closes it all; Save reads the day again (onChanged), which draws the new note in its place.
      placed = el('span', {})
      const ed = editor({ field: f, text: '' }, cl, placed, onChanged, true)
      wrap.append(ed)
      const cancel = Array.from(ed.querySelectorAll<HTMLButtonElement>('button')).find((b) => b.textContent === 'Cancel')
      cancel?.addEventListener('click', (ev) => { ev.stopPropagation(); shut() })
    })
    chips.append(chip)
  }
  open.addEventListener('click', (e) => {
    e.stopPropagation()
    chips.hidden = false
    open.hidden = true
    open.setAttribute('aria-expanded', 'true')
    ;(chips.querySelector('.access-note-chip') as HTMLButtonElement | null)?.focus()
  })
  wrap.append(open, chips)
  return wrap
}
